/**
 * BulkQrPrintDialog — print Site QR posters for several active jobs in
 * one click (Jun 2026). Admin/supervisor picks jobs, backend generates
 * tokens for any that lack one, and a print window renders one poster
 * per page.
 *
 * Backend: POST /api/jobs/site-qr/bulk  { job_ids: [...] } → { posters }
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { QrCode, Printer, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.__BACKEND_URL__}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ACTIVE_STATUSES = ['todo', 'pending', 'accepted', 'in_progress', 'paused'];
const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export default function BulkQrPrintDialog({ open, onClose, jobs }) {
  const [selected, setSelected] = useState({});
  const [query, setQuery] = useState('');
  const [busy, setBusy] = useState(false);

  const activeJobs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (jobs || [])
      .filter((j) => ACTIVE_STATUSES.includes(j.status) && !j.archived && !j.deleted_at)
      .filter((j) => !q
        || (j.title || '').toLowerCase().includes(q)
        || (j.job_number || '').toLowerCase().includes(q)
        || (j.customer_name || '').toLowerCase().includes(q));
  }, [jobs, query]);

  const selectedIds = Object.keys(selected).filter((id) => selected[id]);
  const allChecked = activeJobs.length > 0 && activeJobs.every((j) => selected[j.id]);

  const toggleAll = () => {
    const next = {};
    if (!allChecked) activeJobs.forEach((j) => { next[j.id] = true; });
    setSelected(next);
  };

  const handlePrint = async (ids = selectedIds) => {
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/jobs/site-qr/bulk`,
        { job_ids: ids },
        { headers: authHeaders() },
      );
      const posters = r.data?.posters || [];
      if (posters.length === 0) {
        toast.error('No posters returned.');
        return;
      }
      const w = window.open('', '_blank', 'width=700,height=900');
      if (!w) {
        toast.error('Pop-up blocked — allow pop-ups to print the QR posters.');
        return;
      }
      const pages = posters.map((p) => `
  <div class="poster">
    <div class="brand">Hines Time · Site Clock-In Code</div>
    <h1>${p.job_number ? esc(p.job_number) + ' — ' : ''}${esc(p.job_title)}</h1>
    ${p.job_address ? `<p class="addr">${esc(p.job_address)}</p>` : ''}
    <img src="${p.qr_png}" alt="Site QR code" />
    <div class="instructions">
      <strong>Workers: scan with your phone camera or the Hines Time app</strong>
      <div class="steps">
        1. When you arrive — scan to clock in.<br/>
        2. When you leave — scan the same code to clock out.
      </div>
    </div>
    <p class="foot">If a new code is generated for this job, this poster becomes invalid.</p>
  </div>`).join('\n');
      w.document.write(`<!DOCTYPE html><html><head><title>Site QR Posters (${posters.length})</title>
<style>
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
  .poster { text-align: center; padding: 36px 24px; page-break-after: always; }
  .poster:last-child { page-break-after: auto; }
  .brand { font-size: 14px; letter-spacing: 3px; text-transform: uppercase; color: #555; margin-bottom: 6px; }
  h1 { font-size: 26px; margin: 0 0 4px; }
  .addr { font-size: 15px; color: #444; margin: 0 0 20px; }
  img { width: 340px; height: 340px; border: 4px solid #111; border-radius: 12px; padding: 10px; }
  .instructions { max-width: 440px; margin: 22px auto 0; font-size: 16px; line-height: 1.5; }
  .steps { text-align: left; display: inline-block; margin-top: 10px; font-size: 15px; line-height: 1.7; }
  .foot { margin-top: 26px; font-size: 11px; color: #888; }
</style></head><body>${pages}
<script>window.onload = function () { window.print(); };</script>
</body></html>`);
      w.document.close();
      toast.success(`${posters.length} poster${posters.length === 1 ? '' : 's'} ready to print.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate posters');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="bulk-qr-dialog">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-[#1E88E5]" />
            <span>Print Site QR Posters</span>
          </DialogTitle>
          <DialogDescription>
            Pick the active jobs you want posters for — one poster per page. Jobs without a
            QR code yet get one automatically (existing codes are never changed).
          </DialogDescription>
        </DialogHeader>

        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by job title, number, or customer…"
          className="flex-shrink-0"
          data-testid="bulk-qr-search"
        />

        <div className="flex items-center justify-between flex-shrink-0 px-1">
          <label className="flex items-center gap-2 text-xs cursor-pointer" style={{ color: '#94A3B8' }}>
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="bulk-qr-select-all" />
            Select all ({activeJobs.length})
          </label>
          <span className="text-xs" style={{ color: '#64748B' }} data-testid="bulk-qr-selected-count">
            {selectedIds.length} selected
          </span>
        </div>

        <div className="flex-1 overflow-y-auto rounded-lg divide-y" style={{ border: '1px solid #2D3748', borderColor: '#2D3748' }}>
          {activeJobs.length === 0 ? (
            <p className="text-sm text-center py-8" style={{ color: '#64748B' }}>No active jobs match.</p>
          ) : activeJobs.map((j) => (
            <label
              key={j.id}
              className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/5"
              style={{ borderColor: '#1F2D3F' }}
              data-testid={`bulk-qr-job-row-${j.id}`}
            >
              <Checkbox
                checked={!!selected[j.id]}
                onCheckedChange={() => setSelected((s) => ({ ...s, [j.id]: !s[j.id] }))}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm text-white truncate">
                  {j.job_number ? <span className="font-mono text-[#1E88E5] mr-2">{j.job_number}</span> : null}
                  {j.title}
                </p>
                <p className="text-[11px] truncate" style={{ color: '#64748B' }}>
                  {j.customer_name || '—'} · {String(j.status).replace(/_/g, ' ')}
                  {j.site_qr_required ? ' · has QR' : ''}
                </p>
              </div>
            </label>
          ))}
        </div>

        <div className="flex justify-between gap-2 flex-shrink-0">
          <Button
            variant="outline"
            onClick={() => {
              const ids = activeJobs.map((j) => j.id);
              if (ids.length > 25 && !window.confirm(`This will generate and print ${ids.length} posters (one page each). Continue?`)) return;
              handlePrint(ids);
            }}
            disabled={busy || activeJobs.length === 0}
            data-testid="bulk-qr-print-all-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            {query.trim() ? `Print these (${activeJobs.length})` : `Print all active (${activeJobs.length})`}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onClose?.()} data-testid="bulk-qr-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={() => handlePrint()}
              disabled={busy || selectedIds.length === 0}
              className="bg-[#1E88E5] hover:bg-[#1976D2] text-white"
              data-testid="bulk-qr-print-btn"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Printer className="w-4 h-4 mr-2" />}
              Print {selectedIds.length > 0 ? `${selectedIds.length} ` : ''}poster{selectedIds.length === 1 ? '' : 's'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
